/**
 * Utility functions for managing SubstantiveProcedures.json in SharePoint
 */

export interface SubstantiveProcedure {
  type: "standard" | "custom" | "module";
  name: string;
  questionId?: string; // For custom procedures
}

export interface SubstantiveProceduresData {
  [workspaceName: string]: {
    [procedureName: string]: any[]; // Array can contain procedure data or be empty
  };
}

/**
 * Load SubstantiveProcedures.json from SharePoint juggernaut folder
 */
export async function loadSubstantiveProcedures(): Promise<SubstantiveProceduresData> {
  try {
    const result = await (window as any).sharePointAPI?.downloadClientJson?.("SubstantiveProcedures.json");
    if (result?.success && result?.data) {
      return result.data as SubstantiveProceduresData;
    }
    return {};
  } catch (error) {
    console.warn("Failed to load SubstantiveProcedures.json, starting from empty", error);
    return {};
  }
}

/**
 * Save SubstantiveProcedures.json to SharePoint juggernaut folder
 */
export async function saveSubstantiveProcedures(
  data: SubstantiveProceduresData
): Promise<{ success: boolean; error?: string }> {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

    const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
      fileContent: base64Content,
      fileName: "SubstantiveProcedures.json",
      referenceValue: "Substantive Procedures Configuration",
      folderName: "juggernaut",
      // fyYear is omitted to use config.fyYear from database via getSharePointConfig()
      sectionList: ["SubstantiveProcedures"],
    });

    if (!uploadResult || !uploadResult.success) {
      return {
        success: false,
        error: uploadResult?.error || "Failed to save SubstantiveProcedures.json",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Failed to save SubstantiveProcedures.json", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Add a procedure to a workspace
 */
export async function addProcedureToWorkspace(
  workspaceName: string,
  procedureName: string,
  procedureType: "standard" | "custom" | "module"
): Promise<{ success: boolean; error?: string }> {
  try {
    const data = await loadSubstantiveProcedures();

    if (!data[workspaceName]) {
      data[workspaceName] = {};
    }

    if (data[workspaceName][procedureName] !== undefined) {
      return {
        success: false,
        error: `Procedure "${procedureName}" already exists in this workspace`,
      };
    }

    data[workspaceName][procedureName] = [];

    return await saveSubstantiveProcedures(data);
  } catch (error) {
    console.error("Failed to add procedure to workspace", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Get all procedures for a workspace
 */
export async function getWorkspaceProcedures(
  workspaceName: string
): Promise<string[]> {
  try {
    const data = await loadSubstantiveProcedures();
    return Object.keys(data[workspaceName] || {});
  } catch (error) {
    console.error("Failed to get workspace procedures", error);
    return [];
  }
}

/**
 * Check if a procedure exists for a workspace
 */
export async function hasProcedure(
  workspaceName: string,
  procedureName: string
): Promise<boolean> {
  try {
    const data = await loadSubstantiveProcedures();
    return !!(data[workspaceName] && data[workspaceName][procedureName] !== undefined);
  } catch (error) {
    console.error("Failed to check procedure existence", error);
    return false;
  }
}

/** Sign-off entry stored in SubstantiveProcedures.json per procedure array */
export interface ProcedureSignoffEntry {
  type: "prep_so" | "rev_so" | "conc_so" | "eqr_so" | "partner_so";
  name: string;
  metadata: string;
}

const SIGNOFF_TYPES: ProcedureSignoffEntry["type"][] = ["prep_so", "rev_so", "conc_so", "eqr_so", "partner_so"];

function isSignoffEntry(x: unknown): x is ProcedureSignoffEntry {
  return (
    typeof x === "object" &&
    x !== null &&
    "type" in x &&
    "name" in x &&
    SIGNOFF_TYPES.includes((x as ProcedureSignoffEntry).type) &&
    typeof (x as ProcedureSignoffEntry).name === "string"
  );
}

/** Extract sign-off entries from a procedure's array value (may contain other items) */
export function getSignoffEntriesFromProcedureArray(arr: unknown[]): ProcedureSignoffEntry[] {
  if (!Array.isArray(arr)) return [];
  return arr.filter(isSignoffEntry);
}

/**
 * Append sign-off entries for one or more procedures in a workspace.
 * Each procedure's value in the JSON must be an array; sign-off entries are pushed to it.
 */
export async function addProcedureSignoffs(
  workspaceName: string,
  procedureNames: string[],
  signoffType: ProcedureSignoffEntry["type"],
  userName: string
): Promise<{ success: boolean; error?: string }> {
  try {
    if (procedureNames.length === 0) {
      return { success: false, error: "No procedures selected" };
    }
    const data = await loadSubstantiveProcedures();
    if (!data[workspaceName]) {
      data[workspaceName] = {};
    }
    const entry: ProcedureSignoffEntry = {
      type: signoffType,
      name: userName,
      metadata: new Date().toISOString(),
    };
    for (const procedureName of procedureNames) {
      let arr = data[workspaceName][procedureName];
      if (!Array.isArray(arr)) {
        arr = [];
        data[workspaceName][procedureName] = arr;
      }
      arr.push(entry);
    }
    return await saveSubstantiveProcedures(data);
  } catch (error) {
    console.error("Failed to add procedure sign-offs", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Load SubstantiveProcedures.json from rbin folder
 */
async function loadSubstantiveProceduresFromRbin(): Promise<SubstantiveProceduresData> {
  try {
    // Use a custom API call to download from rbin folder
    // We'll need to create a backend handler for this, but for now use uploadFile pattern
    // Actually, we can use downloadClientJson but it's hardcoded to juggernaut folder
    // So we'll need to create a new handler or modify existing one
    // For now, let's create a helper that uses the backend directly
    const result = await (window as any).sharePointAPI?.downloadRbinJson?.("SubstantiveProcedures.json");
    if (result?.success && result?.data) {
      return result.data as SubstantiveProceduresData;
    }
    return {};
  } catch (error) {
    console.warn("Failed to load SubstantiveProcedures.json from rbin, starting from empty", error);
    return {};
  }
}

/**
 * Save SubstantiveProcedures.json to rbin folder
 */
async function saveSubstantiveProceduresToRbin(
  data: SubstantiveProceduresData
): Promise<{ success: boolean; error?: string }> {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const base64Content = btoa(unescape(encodeURIComponent(jsonString)));

    const uploadResult = await (window as any).sharePointAPI?.uploadFile?.({
      fileContent: base64Content,
      fileName: "SubstantiveProcedures.json",
      referenceValue: "Substantive Procedures Configuration (Deleted)",
      folderName: "rbin",
      // fyYear is omitted to use config.fyYear from database via getSharePointConfig()
      sectionList: ["SubstantiveProcedures"],
    });

    if (!uploadResult || !uploadResult.success) {
      return {
        success: false,
        error: uploadResult?.error || "Failed to save SubstantiveProcedures.json to rbin",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Failed to save SubstantiveProcedures.json to rbin", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

/**
 * Delete procedures from a workspace and move them to rbin folder
 */
export async function deleteProceduresFromWorkspace(
  workspaceName: string,
  procedureNames: string[]
): Promise<{ success: boolean; error?: string }> {
  try {
    // Load data from juggernaut folder
    const juggernautData = await loadSubstantiveProcedures();
    
    if (!juggernautData[workspaceName]) {
      return {
        success: false,
        error: `Workspace "${workspaceName}" not found`,
      };
    }

    // Collect procedures to be deleted
    const proceduresToDelete: Record<string, any> = {};
    procedureNames.forEach((procName) => {
      if (juggernautData[workspaceName][procName] !== undefined) {
        proceduresToDelete[procName] = juggernautData[workspaceName][procName];
        // Remove from juggernaut data
        delete juggernautData[workspaceName][procName];
      }
    });

    if (Object.keys(proceduresToDelete).length === 0) {
      return {
        success: false,
        error: "No procedures found to delete",
      };
    }

    // Save updated juggernaut data
    const saveResult = await saveSubstantiveProcedures(juggernautData);
    if (!saveResult.success) {
      return saveResult;
    }

    // Load rbin data
    const rbinData = await loadSubstantiveProceduresFromRbin();
    
    // Add deleted procedures to rbin data
    if (!rbinData[workspaceName]) {
      rbinData[workspaceName] = {};
    }
    
    Object.keys(proceduresToDelete).forEach((procName) => {
      rbinData[workspaceName][procName] = proceduresToDelete[procName];
    });

    // Save rbin data
    const rbinSaveResult = await saveSubstantiveProceduresToRbin(rbinData);
    if (!rbinSaveResult.success) {
      return rbinSaveResult;
    }

    return { success: true };
  } catch (error) {
    console.error("Failed to delete procedures from workspace", error);
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    };
  }
}

